import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
const PostTable = () => import('@cms/components/tables/post-table/post-table.vue');
const ResourceTable = () => import('@centers/components/tables/resource-table/resource-table.vue');
import DisciplineTable from '@centers/components/tables/discipline-table/discipline-table.vue';
import ActivityTable from '@activities/components/tables/activity-table/activity-table.vue';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import EntityTable from '@shared/components/tables/entity-table/entity-table.vue';
import { Config } from '@shared/interfaces/config';
import { Observable } from 'rxjs';
import { Post } from '@cms/interfaces/post';

@Component({
  name: 'bpc-widget-details',
  components: {
    'bpc-entity-table': EntityTable,
    'bpc-post-table': PostTable,
    'bpc-resource-table': ResourceTable,
    'bpc-discipline-table': DisciplineTable,
    'bpc-activity-table': ActivityTable,
  },
})
export default class WidgetDetails extends Vue {
  @Prop(String) readonly entity: string;
  @Prop({ type: Array, default: () => [] }) readonly entities;
  @Prop({ type: String, default: 'grid' }) readonly initialView: string;
  @Prop({ type: Number, default: 15 }) readonly postsPerPage: number;
  @Prop({ type: String, default: 'publish_start_date_time' }) readonly orderBy: string;
  @Prop({ type: String, default: 'desc' }) readonly orderDirection: string;
  @Prop({ type: Number, default: 4 }) readonly columnNumber: number;
  @Prop({ type: Number, default: null }) readonly postId: number;
  @Prop({ type: Number, default: null }) readonly postCategoryId: number; // Post widget filter
  @Prop({ type: Number, default: null }) readonly activityCategoryId: number; // Activity widget filter
  @Prop({ type: String, default: null }) readonly activityType: string; // Activity widget filter
  @Prop({ type: String, default: null }) readonly activityPeriod: string; // Activity widget filter
  @Prop({ type: Number, default: null }) readonly disciplineId: number; // Resource widget filter
  @Prop({ type: Number, default: null }) readonly resourceId: number; // Discipline widget filter
  @Prop({ type: Number, default: null }) readonly contactTagId: number; // Contact widget filter
  @Prop({ type: String, default: null }) readonly contactType: string; // Contact widget filter

  public widgetSubscription = null;
  public items: any = [];
  public loading: boolean = false;
  public fields: any = [];
  public configLoaded: boolean = false;
  public postSubscription = null;
  public post: Post = null;

  constructor() {
    super();
  }

  get gridCols() {
    return {
      sm: 12,
      md: Math.floor(12 / this.columnNumber),
      lg: Math.floor(12 / this.columnNumber),
    };
  }

  get paginationObject() {
    return {
      per_page: this.postsPerPage,
    };
  }

  get entityOrderBy() {
    if (this.orderBy === 'name') {
      switch (this.entity) {
        case 'post':
          return 'title';
        case 'contact':
          return 'fullname';
      }
    }
    return this.orderBy;
  }

  onRowAction($event: { event: string; item: any }) {
    switch ($event.event) {
      case 'details':
        this.$router.push({
          name: 'public-' + this.sanitizeEntity(this.entity) + '-details',
          params: { alias: $event.item.item.alias },
        });
        break;
    }
  }

  mounted() {
    this.getFields();
    this.getWidgetEntities();
    this.getSeeMorePost();
  }

  getWidgetEntities() {
    this.loading = true;
    if (this.widgetSubscription) {
      this.widgetSubscription.unsubscribe();
    }
    const ids = this.entities ? this.entities.map(entity => entity.id) : [];
    const params = {
      id: ids ? ids : null,
      per_page: this.postsPerPage ? this.postsPerPage : 15,
      orderBy: this.entityOrderBy === 'random' ? '' : this.entityOrderBy,
      orderDirection: this.orderDirection,
      status: this.entity === 'post' ? 'published' : 'active',
      published: 1,
      publish_date: 1,
      randomOrder: this.entityOrderBy === 'random',
      post_category_id: this.entity === 'post' ? this.postCategoryId : null,
      activity_category_id: this.entity === 'bpc-activity' ? this.activityCategoryId : null,
      activity_type: this.entity === 'bpc-activity' ? this.activityType : null,
      period: this.entity === 'bpc-activity' ? this.activityPeriod : null,
      resources__id: this.entity === 'discipline' ? this.resourceId : null,
      disciplines__id: this.entity === 'resource' ? this.disciplineId : null,
      contactTags__id: this.entity === 'contact' ? this.contactTagId : null,
      type: this.entity === 'contact' ? this.contactType : null,
    };
    const queryConfig = {
      params: params,
    };
    const request = this.$bpcApi.get(
      this.$bpcApiRouteHelper.getEntityPath(this.entity),
      queryConfig,
    );
    this.widgetSubscription = request.subscribe(
      (res: BPCGenericApiResponse) => {
        this.items = res.data;
        this.$emit('on-load-items');
        this.loading = false;
      },
      error => {
        console.log(error);
        this.loading = false;
      },
    );
  }

  getSeeMorePost() {
    this.loading = true;
    const fetchEntityPath = `${this.$bpcApiRouteHelper.getEntityPath('posts', 'raw')}/${
      this.postId
    }`;

    const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.get(
      fetchEntityPath,
      null,
      'public',
    );

    if (this.postSubscription) {
      this.postSubscription.unsubscribe();
    }

    this.postSubscription = observable.subscribe(
      (res: BPCGenericApiResponse) => {
        this.post = res.data;
        this.loading = false;
      },
      error => {
        console.log(error);
        this.loading = false;
      },
    );
  }

  public redirectToEntity() {
    const url = this.$clientRouter.getClientUrl(`/posts/${this.post.alias}`);
    this.$router.push(url);
  }

  async getFields() {
    const res = await this.$bpcConfig.getEntityConfig(this.entity).toPromise();
    const config: Config = res.data;
    const archiveFields = config?.displays?.archive?.fields;
    for (const field of archiveFields) {
      if (!field['conditionalShow']) {
        this.fields.push(field);
      } else {
        const keys = field['conditionalShow']['fieldPath'].split('.');
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let conditionalValue = this;
        for (const key of keys) {
          if (conditionalValue[key] != 'undefined') {
            conditionalValue = conditionalValue[key];
          } else {
            break;
          }
        }
        if (conditionalValue === field['conditionalShow']['value']) {
          this.fields.push(field);
        }
      }
    }
    this.configLoaded = true;
  }

  sanitizeEntity(entity) {
    // We need bpc-activity to load the items but only activity to load the single
    return entity.replace('bpc-', '');
  }
}
